import { gql } from "@apollo/client";

import { COMPLIANCE_BUNDLE_FIELDS } from "./complianceBundles";
import { TOLL_FREE_REGISTRATION_STAFF_FIELDS } from "./tollFreeRegistrations";

export const TENDLC_CAMPAIGN_REGISTRATION_FIELDS = gql`
  fragment TenDlcCampaignRegistrationFields on TenDlcCampaignRegistration {
    id
    brandRegistrationId
    useCase
    description
    embeddedPhone
    optoutMessage
    helpMessage
    termsAndConditionsLink
    optInConfirmationMessage
    acknowledgedCompliance
    createComplianceBundle
    optInLink
    privacyPolicyLink
    sampleScripts
    usageStartDate
    usageTypes
    additionalContext
    tenDlcCampaign {
      tcrCampaignId
      readyEmailSentAt
      createdAt
    }
    originalValues {
      useCase
      description
      embeddedPhone
      optoutMessage
      helpMessage
      termsAndConditionsLink
      optInConfirmationMessage
      acknowledgedCompliance
      createComplianceBundle
      optInLink
      privacyPolicyLink
      sampleScripts
      additionalContext
      usageStartDate
      usageTypes
    }

    tenantId
    tenant {
      id
      slug
    }
    subaccountId
    subaccount {
      id
      slug
    }

    submittedAt
    submittedByUser {
      id
      fullName
    }

    createdByUser {
      id
      fullName
    }

    createdAt
    updatedAt
  }
`;

export const TENDLC_CAMPAIGN_REGISTRATION_STAFF_FIELDS = gql`
  ${COMPLIANCE_BUNDLE_FIELDS}
  fragment TenDlcCampaignRegistrationStaffFields on TenDlcCampaignRegistration {
    status
    useCase
    tenDlcCampaign {
      tcrCampaignId
    }

    friendlyName
    messageFlowStaff
    tcrCampaignId
    tcrStatus
    tcrUrl

    expediteStatus
    providerName
    assignedToStaffEmail
    isDirty

    submittedByUser {
      id
      fullName
    }

    complianceBundle {
      id
      ...ComplianceBundleFields
    }

    events {
      id
      type
      createdAt
      ... on RegistrationEditedEvent {
        data {
          submittedByStaffEmail
        }
      }
      ... on RegistrationSubmittedToTcrEvent {
        data {
          submittedByStaffEmail
          payload {
            version
            firstName
            lastName
            displayName
            companyName
            ein
            einIssuingCountry
            street
            city
            state
            postalCode
            country
            email
            website
            phone
            brandRelationship
            vertical
          }
        }
      }
      ... on RegistrationSubmittedCampaignToTcrEvent {
        data {
          submittedByStaffEmail
          payload {
            brandId
            usecase
            resellerId
            description
            embeddedPhone
            messageFlow
            optoutMessage
            helpMessage
            termsAndConditionsLink
            optinMessage
            sample1
            sample2
            sample3
            sample4
            privacyPolicyLink
          }
        }
      }
      ... on RegistrationSubmissionToTcrFailedEvent {
        data {
          submissionEventId
          error
        }
      }
      ... on RegistrationExpediteRequestedEvent {
        data {
          submittedByStaffEmail
        }
      }
      ... on RegistrationExpeditePaidEvent {
        data {
          submittedByStaffEmail
        }
      }
      ... on RegistrationCampaignShareRequestedEvent {
        data {
          submittedByStaffEmail
        }
      }
      ... on RegistrationCampaignShareRejectedEvent {
        data {
          error
        }
      }
      ... on RegistrationCampaignShareAcceptedEvent {
        data {
          description
        }
      }
      ... on RegistrationCampaignDcaCompletedEvent {
        data {
          description
        }
      }
      ... on RegistrationBrandIdentityStatusUpdateEvent {
        data {
          brandIdentityStatus
        }
      }
      ... on RegistrationCampaignCopiedEvent {
        data {
          copiedByStaffEmail
          copiedToCampaignId
          copiedToTollFreeId
        }
      }
    }
  }
`;

export const TENDLC_BRAND_REGISTRATION_FIELDS = gql`
  fragment TenDlcBrandRegistrationFields on TenDlcBrandRegistration {
    id
    tenantId
    subaccountId
    organizationName
    displayName
    orgType
    ein
    einIssuingCountry
    cvToken
    focusArea
    expectedTexts
    desiredAreaCodes
    preferredAreaCode
    addressOne
    addressTwo
    city
    state
    postalCode
    country
    primaryContactName
    primaryContactPhone
    primaryContactEmail
    organizationContactName
    organizationContactPhone
    organizationContactEmail
    website
    tcrBrandId

    originalValues {
      organizationName
      displayName
      orgType
      ein
      einIssuingCountry
      cvToken
      focusArea
      expectedTexts
      desiredAreaCodes
      preferredAreaCode
      addressOne
      addressTwo
      city
      state
      postalCode
      country
      primaryContactName
      primaryContactPhone
      primaryContactEmail
      organizationContactName
      organizationContactPhone
      organizationContactEmail
      website
    }

    submittedAt
    submittedByUser {
      id
      fullName
    }

    createdByUser {
      id
      fullName
    }

    createdAt
    updatedAt
  }
`;

export const TENDLC_BRAND_REGISTRATION_STAFF_FIELDS = gql`
  fragment TenDlcBrandRegistrationStaffFields on TenDlcBrandRegistration {
    tcrIdentityStatus
    tcrVettingStatus
    taxExemptStatus
    vettingScore
    tcrUrl

    tcrUseCases {
      usecase
      annualFee
      monthlyFee
      quarterlyFee
      mnoMetadata {
        ... on AttMetadata {
          mno
          qualify
          tpm
          mmsTpm
        }
        ... on TmobileMetadata {
          mno
          qualify
          brandDailyCap
          brandTier
        }
        ... on OtherMetadata {
          mno
          qualify
        }
      }
    }

    assignedToStaffEmail

    submittedByUser {
      id
      fullName
    }

    events {
      id
      type
      createdAt
      ... on RegistrationEditedEvent {
        data {
          submittedByStaffEmail
        }
      }
      ... on RegistrationSubmittedToTcrEvent {
        data {
          submittedByStaffEmail
          payload {
            version
            firstName
            lastName
            displayName
            companyName
            ein
            einIssuingCountry
            street
            city
            state
            postalCode
            country
            email
            website
            phone
            brandRelationship
            vertical
          }
        }
      }
      ... on RegistrationSubmissionToTcrFailedEvent {
        data {
          submissionEventId
          error
        }
      }
      ... on RegistrationExpediteRequestedEvent {
        data {
          submittedByStaffEmail
        }
      }
      ... on RegistrationExpeditePaidEvent {
        data {
          submittedByStaffEmail
        }
      }
      ... on RegistrationBrandImportedFromTcrEvent {
        data {
          submittedByStaffEmail
          payload {
            firstName
            lastName
            displayName
            companyName
            ein
            einIssuingCountry
            street
            city
            state
            postalCode
            country
            email
            website
            phone
            brandRelationship
            vertical
          }
        }
      }
    }
  }
`;

export const GET_REGISTRATION = gql`
  ${TENDLC_BRAND_REGISTRATION_FIELDS}
  ${TENDLC_CAMPAIGN_REGISTRATION_FIELDS}
  query getRegistration($input: GetRegistrationInput!) {
    getRegistration(input: $input) {
      ...TenDlcCampaignRegistrationFields
      brandRegistration {
        ...TenDlcBrandRegistrationFields
      }
    }
  }
`;

export const LIST_BRAND_REGISTRATIONS = gql`
  ${TENDLC_BRAND_REGISTRATION_FIELDS}
  ${TENDLC_CAMPAIGN_REGISTRATION_FIELDS}
  query listBrandRegistrations($input: ListBrandRegistrationsInput!) {
    listBrandRegistrations(input: $input) {
      ...TenDlcBrandRegistrationFields
      campaignRegistrations {
        ...TenDlcCampaignRegistrationFields
      }
    }
  }
`;

export const LIST_CAMPAIGN_REGISTRATIONS = gql`
  ${TENDLC_BRAND_REGISTRATION_FIELDS}
  ${TENDLC_CAMPAIGN_REGISTRATION_FIELDS}
  query listCampaignRegistrations($input: ListCampaignRegistrationsInput!) {
    listCampaignRegistrations(input: $input) {
      ...TenDlcCampaignRegistrationFields
      brandRegistration {
        ...TenDlcBrandRegistrationFields
      }
    }
  }
`;

export const LIST_ALL_TEN_DLC_REGISTRATIONS = gql`
  ${TENDLC_BRAND_REGISTRATION_FIELDS}
  ${TENDLC_BRAND_REGISTRATION_STAFF_FIELDS}
  ${TENDLC_CAMPAIGN_REGISTRATION_FIELDS}
  ${TENDLC_CAMPAIGN_REGISTRATION_STAFF_FIELDS}
  query listAllTenDlcRegistrations($input: ListAllTenDlcRegistrationsInput!) {
    listAllTenDlcRegistrations(input: $input) {
      ...TenDlcBrandRegistrationFields
      ...TenDlcBrandRegistrationStaffFields
      campaignRegistrations {
        ...TenDlcCampaignRegistrationFields
        ...TenDlcCampaignRegistrationStaffFields
      }
    }
  }
`;

export const LIST_ALL_TEN_DLC_CAMPAIGN_REGISTRATIONS = gql`
  ${TENDLC_BRAND_REGISTRATION_FIELDS}
  ${TENDLC_BRAND_REGISTRATION_STAFF_FIELDS}
  ${TENDLC_CAMPAIGN_REGISTRATION_FIELDS}
  ${TENDLC_CAMPAIGN_REGISTRATION_STAFF_FIELDS}
  query listAllTenDlcCampaignRegistrations(
    $input: ListAllTenDlcCampaignRegistrationsInput!
  ) {
    listAllTenDlcCampaignRegistrations(input: $input) {
      totalCount
      registrations {
        ...TenDlcCampaignRegistrationFields
        ...TenDlcCampaignRegistrationStaffFields
        brandRegistration {
          ...TenDlcBrandRegistrationFields
          ...TenDlcBrandRegistrationStaffFields
        }
        tenant {
          id
          slug
          name
        }
        subaccount {
          id
          slug
        }
      }
    }
  }
`;

export const GET_REGISTRATION_FOR_STAFF = gql`
  ${TENDLC_BRAND_REGISTRATION_FIELDS}
  ${TENDLC_BRAND_REGISTRATION_STAFF_FIELDS}
  ${TENDLC_CAMPAIGN_REGISTRATION_FIELDS}
  ${TENDLC_CAMPAIGN_REGISTRATION_STAFF_FIELDS}
  query getRegistrationForStaff($input: GetRegistrationForStaffInput!) {
    getRegistrationForStaff(input: $input) {
      ...TenDlcBrandRegistrationFields
      ...TenDlcBrandRegistrationStaffFields
      campaignRegistrations {
        ...TenDlcCampaignRegistrationFields
        ...TenDlcCampaignRegistrationStaffFields
      }
    }
  }
`;

export const CREATE_TENDLC_BRAND_AND_CAMPAIGN_REGISTRATION = gql`
  ${TENDLC_BRAND_REGISTRATION_FIELDS}
  ${TENDLC_CAMPAIGN_REGISTRATION_FIELDS}
  mutation Create10DlcBrandAndCampaignRegistration(
    $input: CreateBrandAndCampaignRegistrationInput!
  ) {
    createBrandAndCampaignRegistration(input: $input) {
      ...TenDlcBrandRegistrationFields
      campaignRegistrations {
        ...TenDlcCampaignRegistrationFields
      }
    }
  }
`;

export const UPDATE_TENDLC_BRAND_AND_CAMPAIGN_REGISTRATION = gql`
  ${TENDLC_BRAND_REGISTRATION_FIELDS}
  ${TENDLC_CAMPAIGN_REGISTRATION_FIELDS}
  mutation Update10DlcBrandAndCampaignRegistration(
    $input: UpdateBrandAndCampaignRegistrationInput!
  ) {
    updateBrandAndCampaignRegistration(input: $input) {
      ...TenDlcBrandRegistrationFields
      campaignRegistrations {
        ...TenDlcCampaignRegistrationFields
      }
    }
  }
`;

export const UPDATE_TENDLC_BRAND_REGISTRATION_FOR_STAFF = gql`
  ${TENDLC_BRAND_REGISTRATION_FIELDS}
  ${TENDLC_BRAND_REGISTRATION_STAFF_FIELDS}
  ${TENDLC_CAMPAIGN_REGISTRATION_FIELDS}
  ${TENDLC_CAMPAIGN_REGISTRATION_STAFF_FIELDS}
  mutation UpdateBrandRegistrationForStaff(
    $input: UpdateBrandRegistrationForStaffInput!
  ) {
    updateBrandRegistrationForStaff(input: $input) {
      ...TenDlcBrandRegistrationFields
      ...TenDlcBrandRegistrationStaffFields
      campaignRegistrations {
        ...TenDlcCampaignRegistrationFields
        ...TenDlcCampaignRegistrationStaffFields
      }
    }
  }
`;

export const UPDATE_TENDLC_CAMPAIGN_REGISTRATION_FOR_STAFF = gql`
  ${TENDLC_CAMPAIGN_REGISTRATION_FIELDS}
  ${TENDLC_CAMPAIGN_REGISTRATION_STAFF_FIELDS}
  mutation UpdateCampaignRegistrationForStaff(
    $input: UpdateCampaignRegistrationForStaffInput!
  ) {
    updateCampaignRegistrationForStaff(input: $input) {
      ...TenDlcCampaignRegistrationFields
      ...TenDlcCampaignRegistrationStaffFields
    }
  }
`;

export const SUBMIT_TENDLC_REGISTRATION = gql`
  ${TENDLC_BRAND_REGISTRATION_FIELDS}
  ${TENDLC_CAMPAIGN_REGISTRATION_FIELDS}
  mutation Submit10DlcRegistration($input: SubmitCampaignRegistrationInput!) {
    submitCampaignRegistration(input: $input) {
      ...TenDlcBrandRegistrationFields
      campaignRegistrations {
        ...TenDlcCampaignRegistrationFields
      }
    }
  }
`;

export const COPY_TENDLC_REGISTRATION = gql`
  ${TENDLC_CAMPAIGN_REGISTRATION_FIELDS}
  ${TENDLC_CAMPAIGN_REGISTRATION_STAFF_FIELDS}
  mutation Copy10DlcRegistration($input: CopyCampaignRegistrationInput!) {
    copyCampaignRegistration(input: $input) {
      ...TenDlcCampaignRegistrationFields
      ...TenDlcCampaignRegistrationStaffFields
    }
  }
`;

export const COPY_TENDLC_REGISTRATION_TO_TOLL_FREE = gql`
  ${TOLL_FREE_REGISTRATION_STAFF_FIELDS}
  mutation Copy10DlcRegistrationToTollFree(
    $input: CopyTenDlcRegistrationToTollFreeInput!
  ) {
    copyTenDlcRegistrationToTollFree(input: $input) {
      ...TollFreeRegistrationStaffFields
    }
  }
`;

export const LOOKUP_TCR_BRAND_BY_EIN = gql`
  query LookupTcrBrandByEin($input: LookupTcrBrandByEinInput!) {
    lookupTcrBrandByEin(input: $input) {
      brandId
      entityType
      cspId
      firstName
      lastName
      displayName
      companyName
      ein
      street
      city
      state
      postalCode
      country
      email
      website
      phone
      brandRelationship
      vertical
      identityStatus
      optionalAttributes {
        section527
        taxExemptStatus
      }
    }
  }
`;

export const GET_LATEST_EVP_REPORT = gql`
  query GetLatestEvpReport($input: GetLatestEvpReportInput!) {
    getLatestEvpReport(input: $input) {
      evpId
      vettingId
      vettingClass
      vettingStatus
      createDate
      vettedDate
    }
  }
`;

export const IMPORT_CV_TOKEN = gql`
  mutation ImportCvToken($input: ImportCvTokenInput!) {
    importCvToken(input: $input) {
      success
    }
  }
`;

export const IMPORT_TCR_BRAND_TO_REGISTRATION = gql`
  ${TENDLC_BRAND_REGISTRATION_FIELDS}
  ${TENDLC_BRAND_REGISTRATION_STAFF_FIELDS}
  ${TENDLC_CAMPAIGN_REGISTRATION_FIELDS}
  ${TENDLC_CAMPAIGN_REGISTRATION_STAFF_FIELDS}
  mutation ImportTcrBrandToRegistration(
    $input: ImportTcrBrandToRegistrationInput!
  ) {
    importTcrBrandToRegistration(input: $input) {
      ...TenDlcBrandRegistrationFields
      ...TenDlcBrandRegistrationStaffFields
      campaignRegistrations {
        ...TenDlcCampaignRegistrationFields
        ...TenDlcCampaignRegistrationStaffFields
      }
    }
  }
`;

export const SUBMIT_BRAND_TO_TCR = gql`
  ${TENDLC_BRAND_REGISTRATION_FIELDS}
  ${TENDLC_BRAND_REGISTRATION_STAFF_FIELDS}
  ${TENDLC_CAMPAIGN_REGISTRATION_FIELDS}
  ${TENDLC_CAMPAIGN_REGISTRATION_STAFF_FIELDS}
  mutation SubmitBrandToTcr($input: SubmitBrandToTcrInput!) {
    submitBrandToTcr(input: $input) {
      ...TenDlcBrandRegistrationFields
      ...TenDlcBrandRegistrationStaffFields
      campaignRegistrations {
        ...TenDlcCampaignRegistrationFields
        ...TenDlcCampaignRegistrationStaffFields
      }
    }
  }
`;

export const SUBMIT_CAMPAIGN_TO_TCR = gql`
  ${TENDLC_CAMPAIGN_REGISTRATION_FIELDS}
  ${TENDLC_CAMPAIGN_REGISTRATION_STAFF_FIELDS}
  mutation SubmitCampaignToTcr($input: SubmitCampaignToTcrInput!) {
    submitCampaignToTcr(input: $input) {
      ...TenDlcCampaignRegistrationFields
      ...TenDlcCampaignRegistrationStaffFields
    }
  }
`;

export const UPDATE_CAMPAIGN_IN_TCR = gql`
  ${TENDLC_CAMPAIGN_REGISTRATION_FIELDS}
  ${TENDLC_CAMPAIGN_REGISTRATION_STAFF_FIELDS}
  mutation UpdateCampaignInTcr($input: UpdateCampaignInTcrInput!) {
    updateCampaignInTcr(input: $input) {
      ...TenDlcCampaignRegistrationFields
      ...TenDlcCampaignRegistrationStaffFields
    }
  }
`;

export const DELETE_TEN_DLC_CAMPAIGN_REGISTRATION = gql`
  mutation DeleteTenDlcCampaignRegistration(
    $input: DeleteTenDlcCampaignRegistrationInput!
  ) {
    deleteTenDlcCampaignRegistration(input: $input) {
      success
    }
  }
`;

export const REUSE_COMPLIANCE_BUNDLE = gql`
  mutation ReuseComplianceBundle($input: ReuseComplianceBundleInput!) {
    reuseComplianceBundle(input: $input) {
      success
    }
  }
`;
